/* eslint-disable complexity */

import type { PopulateParam } from '@/interfaces/strapi';
import { buildFilters } from './buildStrapiUrl';

/**
 * Serializa populate profundo con soporte para:
 * - booleanos: populate[rel]=true
 * - arrays:   populate[rel][populate]=a,b
 * - fields:   populate[rel][fields][0]=url ...
 * - sort:     populate[rel][sort][0]=order:asc ...
 * - filters:  populate[rel][filters][...]
 * - objetos anidados dentro de 'populate'
 */
export function buildPopulate(pop: PopulateParam, prefix = 'populate'): string[] {
	// Caso array tope: ['rel1','rel2'] => populate[relX]=true
	if (Array.isArray(pop)) {
		return pop.map((rel) => `${prefix}[${encodeURIComponent(rel)}]=true`);
	}

	// Caso objeto: { rel: config, ... }
	const out: string[] = [];
	for (const [relation, config] of Object.entries(pop)) {
		const relKey = `${prefix}[${encodeURIComponent(relation)}]`;

		if (config === true) {
			out.push(`${relKey}=true`);
			continue;
		}

		if (Array.isArray(config)) {
			// Populate[rel][populate]=a,b
			out.push(`${relKey}[populate]=${config.map(encodeURIComponent).join(',')}`);
			continue;
		}

		if (typeof config === 'object' && config) {
			// 1) fields
			if (Array.isArray((config as any).fields)) {
				(config as any).fields.forEach((f: string, i: number) => {
					out.push(`${relKey}[fields][${i}]=${encodeURIComponent(f)}`);
				});
			}

			// 2) sort
			if (Array.isArray((config as any).sort)) {
				(config as any).sort.forEach((s: string, i: number) => {
					out.push(`${relKey}[sort][${i}]=${encodeURIComponent(s)}`);
				});
			}

			// 3) filters
			if ((config as any).filters && typeof (config as any).filters === 'object') {
				out.push(...buildFilters(`${relKey}[filters]`, (config as any).filters));
			}

			// 4) populate: puede ser array (sub1,sub2) o objeto anidado
			const sub = (config as any).populate;
			if (Array.isArray(sub)) {
				out.push(`${relKey}[populate]=${sub.map(encodeURIComponent).join(',')}`);
			} else if (typeof sub === 'object' && sub) {
				out.push(...buildPopulate(sub, `${relKey}[populate]`));
			}

			// 5) Cualquier otra clave-objeto la tratamos como otra relación anidada
			for (const [k, v] of Object.entries(config as any)) {
				if (k === 'fields' || k === 'sort' || k === 'filters' || k === 'populate') continue;
				if (v === true) {
					out.push(`${relKey}[${encodeURIComponent(k)}]=true`);
				} else if (Array.isArray(v)) {
					out.push(`${relKey}[${encodeURIComponent(k)}][populate]=${v.map(encodeURIComponent).join(',')}`);
				} else if (typeof v === 'object' && v) {
					out.push(...buildPopulate({ [k]: v } as any, relKey));
				}
			}
		}
	}
	return out;
}
